<template>
  <div class="flex flex-col py-4 text-base md:flex-row md:gap-10">
    <NuxtLink
      class="max-w-[290px] self-center md:w-1/4 md:max-w-none md:self-auto"
      :to="productPath"
      :title="productConfig.name"
      @click="trackSelectItem(product)"
    >
      <ProductImage
        :image="productImage"
        :alt="productConfig.name"
        class="mx-auto my-4 max-w-xs mix-blend-multiply"
      />
      <ProductPromotionBadges :product="product" view="basket" class="mt-2" />
      <ProductPromotionFreeGiftBadge
        v-if="isFreeGift"
        :style="promotionStyle"
        class="absolute bottom-2 left-2"
      />
    </NuxtLink>
    <div class="min-w-[210px] md:w-3/4">
      <ProductConfigurationList
        data-testid="rxConfigurationList"
        class="pb-4 sm:pb-4"
        :product="productConfig"
        :product-configuration="productConfigItems"
        :product-path="productPath"
        :description="productColorAndSize"
        :quantity="quantity"
        :is-quantity-change-disabled="hasQuantityChangeDisabled"
        @click:change-quantity="
          (quantity: number) => $emit('click:change-quantity', quantity)
        "
        @click:remove="$emit('click:delete')"
        @click:edit="
          (step: ProductConfigurationRxItem) =>
            $emit('click:edit-configuration-step', step.masterKey)
        "
      />
      <div
        class="flex flex-col justify-between sm:flex-row sm:gap-x-6 lg:gap-x-12"
      >
        <div class="relative w-full">
          <ClientOnly>
            <SightData
              v-if="supportsPrescriptionValues"
              :openable="true"
              class="transition-opacity"
              :class="{ 'opacity-30': fetching }"
              :title="$t('rxbasketcardDefault.sightData.title')"
              :details="prescriptionValues"
              :hide-table="!prescriptionValues"
              :start-open="!prescriptionValues"
            >
              <template v-if="prescriptionValues" #actions>
                <div class="flex gap-3">
                  <CircleButton
                    id="editPrescriptionValues"
                    :size="8"
                    background-color-class="bg-white"
                    background-color-hover-class="hover:bg-white"
                    data-testid="editPrescription"
                    :aria-label="$t('rxbasketcardDefault.sightData.edit')"
                    :label="$t('rxbasketcardDefault.sightData.edit')"
                    :title="$t('rxbasketcardDefault.sightData.edit')"
                    no-margin
                    border-color="[#D7DDE3]"
                    border-color-hover="black"
                    @mousedown="openEditPrescriptionValuesOverlay"
                  >
                    <template #icon>
                      <LazyIconFielmannEdit class="size-5" />
                    </template>
                  </CircleButton>
                </div>
              </template>
              <template v-else #actions>
                <div class="flex justify-center">
                  <FimButton
                    id="addPrescriptionValues"
                    @click="openEditPrescriptionValuesOverlay"
                  >
                    {{ $t('rx.data_table.enter_prescription') }}
                  </FimButton>
                </div>
              </template>
            </SightData>
            <div
              v-if="fetching"
              class="absolute left-0 top-0 z-10 size-full overflow-hidden rounded-lg opacity-60"
            >
              <Skeleton full-width full-height />
            </div>
          </ClientOnly>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type {
  BasketItemDisplayData,
  Variant,
  Product as BapiProduct,
} from '@scayle/storefront-nuxt'
import useUiState from '~/composables/ui/useUiState'
import {
  type FimBasketItemCustomData,
  type ItemGroupMainItem,
  useFimBasket,
} from '~/composables/useFimBasket'
import type { ConfiguratorSerialized } from '~/composables/useRxConfiguratorState'

import type { ProductConfigurationRxItem } from '~/components/ui/productConfiguration/ProductConfigurationItem'

const props = defineProps({
  basketItem: {
    type: Object as PropType<FimBasketItem>,
    required: true,
  },
  product: {
    type: Object as PropType<BapiProduct>,
    required: true,
  },
  variant: {
    type: Object as PropType<Variant>,
    required: true,
  },
  quantity: {
    type: Number as PropType<number>,
    default: 1,
  },
  displayData: {
    type: Object as PropType<BasketItemDisplayData>,
    default: undefined,
  },
  customData: {
    type: Object as PropType<FimBasketItemCustomData>,
    required: true,
  },
  itemGroup: {
    type: Object as PropType<ItemGroupMainItem>,
    required: true,
  },
  configuratorItems: {
    type: Array as PropType<ConfiguratorSerialized>,
    required: true,
  },
})

const emit = defineEmits([
  'click:change-quantity',
  'click:delete',
  'click:edit-configuration-step',
  'click:edit-prescription-values',
])

const { $i18n, $fimLocalePath } = useNuxtApp()
const { trackSelectItem, trackButtonClick } = await useTrackingEvents()

const basket = await useFimBasket()

const basketItem = computed(() => props.basketItem)

const { isFreeGift, promotionStyle } = await useBasketItemPromotion(basketItem)

const { getProductDetailRoute } = useRouteHelpers()
const { openRxEditPrescriptionOverlay } = useUiState()

const productConfig = computed(() =>
  getProductConfigRxMainItem(
    props.product,
    props.variant,
    props.basketItem.price.unit,
    props.displayData,
  ),
)

const supportsPrescriptionValues = computed(
  () =>
    isRxGlassesWithoutPrescriptionValues(basketItem.value) ||
    Boolean(props.customData?.prescriptionValues),
)

const prescriptionValues = computed(() =>
  isDefined(props.customData?.prescriptionValues?.left.sphere)
    ? props.customData.prescriptionValues
    : undefined,
)

const productColorAndSize = getFrameColorAndSize(props.product, props.variant)

// Don't want to show edit button for free in store service item
const productConfigItems = computed(() =>
  getProductConfigRxItems(
    props.basketItem.price.unit,
    props.customData.configurationItems ?? [],
    $i18n,
  ),
)

const openEditPrescriptionValuesOverlay = async (event: Event) => {
  emit('click:edit-prescription-values')
  const element = event?.target as HTMLButtonElement
  trackButtonClick(element)

  openRxEditPrescriptionOverlay(props.itemGroup?.id)
}

const productPath = computed(() =>
  $fimLocalePath(
    getProductDetailRoute(props.product, {
      variantid: props.variant.id,
    }),
  ),
)

const fetching = computed(() => unref(basket.fetching))

const productImage = computed(() => getFirstProductImage(props.product))
const hasQuantityChangeDisabled = computed(() =>
  isQuantityChangeDisabled(props.product),
)
</script>
